import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const HoldingsTable = ({ holdings, selectedCurrency, onExport }) => {
  const [sortBy, setSortBy] = useState('value');
  const [sortOrder, setSortOrder] = useState('desc');

  const formatCurrency = (amount, currency = selectedCurrency) => {
    const symbols = { USD: '$', EUR: '€', BTC: '₿', ETH: 'Ξ' };
    const decimals = currency === 'BTC' || currency === 'ETH' ? 6 : 2;
    return `${symbols?.[currency]}${amount?.toLocaleString('en-US', { 
      minimumFractionDigits: decimals, 
      maximumFractionDigits: decimals 
    })}`;
  };

  const formatQuantity = (quantity) => {
    return quantity?.toLocaleString('en-US', { 
      minimumFractionDigits: 2, 
      maximumFractionDigits: 8 
    });
  };

  const getChangeColor = (change) => {
    if (change > 0) return 'text-success';
    if (change < 0) return 'text-error';
    return 'text-muted-foreground';
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  const sortedHoldings = [...holdings]?.sort((a, b) => {
    let aValue = a?.[sortBy];
    let bValue = b?.[sortBy];
    
    if (typeof aValue === 'string') {
      aValue = aValue?.toLowerCase();
      bValue = bValue?.toLowerCase();
    }
    
    if (sortOrder === 'asc') {
      return aValue > bValue ? 1 : -1;
    } else {
      return aValue < bValue ? 1 : -1;
    }
  });

  const SortButton = ({ field, children }) => (
    <button
      onClick={() => handleSort(field)}
      className="flex items-center space-x-1 text-left hover:text-foreground transition-colors"
    >
      <span>{children}</span>
      {sortBy === field && (
        <Icon 
          name={sortOrder === 'asc' ? 'ChevronUp' : 'ChevronDown'} 
          size={16} 
        />
      )}
    </button>
  );

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      <div className="flex items-center justify-between p-6 border-b border-border">
        <h3 className="text-lg font-semibold text-foreground">Holdings</h3>
        <Button
          variant="outline"
          size="sm"
          onClick={onExport}
          iconName="Download"
          iconPosition="left"
        >
          Export
        </Button>
      </div>
      {/* Desktop Table */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/50">
            <tr>
              <th className="text-left p-4 text-sm font-medium text-muted-foreground">
                <SortButton field="name">Asset</SortButton>
              </th>
              <th className="text-right p-4 text-sm font-medium text-muted-foreground">
                <SortButton field="price">Price</SortButton>
              </th>
              <th className="text-right p-4 text-sm font-medium text-muted-foreground">
                <SortButton field="quantity">Holdings</SortButton>
              </th>
              <th className="text-right p-4 text-sm font-medium text-muted-foreground">
                <SortButton field="value">Market Value</SortButton>
              </th>
              <th className="text-right p-4 text-sm font-medium text-muted-foreground">
                <SortButton field="change24h">24h Change</SortButton>
              </th>
              <th className="text-right p-4 text-sm font-medium text-muted-foreground">
                <SortButton field="pnl">P&L</SortButton>
              </th>
              <th className="text-right p-4 text-sm font-medium text-muted-foreground">
                <SortButton field="allocation">Allocation</SortButton>
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedHoldings?.map((holding) => (
              <tr key={holding?.id} className="border-b border-border hover:bg-muted/30 transition-colors">
                <td className="p-4">
                  <div className="flex items-center space-x-3">
                    <Image
                      src={holding?.icon}
                      alt={holding?.name}
                      className="w-8 h-8 rounded-full"
                    />
                    <div>
                      <p className="font-medium text-foreground">{holding?.name}</p>
                      <p className="text-sm text-muted-foreground">{holding?.symbol}</p>
                    </div>
                  </div>
                </td>
                <td className="p-4 text-right">
                  <p className="font-medium text-foreground">{formatCurrency(holding?.price)}</p>
                </td>
                <td className="p-4 text-right">
                  <p className="font-medium text-foreground">{formatQuantity(holding?.quantity)}</p>
                  <p className="text-sm text-muted-foreground">{holding?.symbol}</p>
                </td>
                <td className="p-4 text-right">
                  <p className="font-medium text-foreground">{formatCurrency(holding?.value)}</p>
                </td>
                <td className="p-4 text-right">
                  <div className={`flex items-center justify-end space-x-1 ${getChangeColor(holding?.change24h)}`}>
                    <Icon 
                      name={holding?.change24h >= 0 ? 'TrendingUp' : 'TrendingDown'} 
                      size={16} 
                    />
                    <span className="font-medium">
                      {holding?.change24h >= 0 ? '+' : ''}{holding?.change24h?.toFixed(2)}%
                    </span>
                  </div>
                </td>
                <td className="p-4 text-right">
                  <div className={getChangeColor(holding?.pnl)}>
                    <p className="font-medium">{formatCurrency(holding?.pnl)}</p>
                    <p className="text-sm">
                      ({holding?.pnlPercent >= 0 ? '+' : ''}{holding?.pnlPercent?.toFixed(2)}%)
                    </p>
                  </div>
                </td>
                <td className="p-4 text-right">
                  <p className="font-medium text-foreground">{holding?.allocation?.toFixed(2)}%</p>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Mobile Cards */}
      <div className="md:hidden space-y-4 p-4">
        {sortedHoldings?.map((holding) => (
          <div key={holding?.id} className="bg-muted/30 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center space-x-3">
                <Image
                  src={holding?.icon}
                  alt={holding?.name}
                  className="w-10 h-10 rounded-full"
                />
                <div>
                  <p className="font-medium text-foreground">{holding?.name}</p>
                  <p className="text-sm text-muted-foreground">{holding?.symbol}</p>
                </div>
              </div>
              <div className="text-right">
                <p className="font-medium text-foreground">{formatCurrency(holding?.value)}</p>
                <p className="text-sm text-muted-foreground">{holding?.allocation?.toFixed(2)}%</p>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-muted-foreground">Price</p>
                <p className="font-medium text-foreground">{formatCurrency(holding?.price)}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Holdings</p>
                <p className="font-medium text-foreground">{formatQuantity(holding?.quantity)}</p>
              </div>
              <div>
                <p className="text-muted-foreground">24h Change</p>
                <div className={`flex items-center space-x-1 ${getChangeColor(holding?.change24h)}`}>
                  <Icon 
                    name={holding?.change24h >= 0 ? 'TrendingUp' : 'TrendingDown'} 
                    size={14} 
                  />
                  <span className="font-medium">
                    {holding?.change24h >= 0 ? '+' : ''}{holding?.change24h?.toFixed(2)}%
                  </span>
                </div>
              </div>
              <div>
                <p className="text-muted-foreground">P&L</p>
                <div className={getChangeColor(holding?.pnl)}>
                  <p className="font-medium">{formatCurrency(holding?.pnl)}</p>
                  <p className="text-xs">
                    ({holding?.pnlPercent >= 0 ? '+' : ''}{holding?.pnlPercent?.toFixed(2)}%)
                  </p>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default HoldingsTable;